import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChartLineUp, Pulse, Target, Lightbulb, Flask, Stack, ArrowRight, CheckCircle, XCircle, TrendUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const CAT_COLORS = ["bg-primary", "bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-violet-500", "bg-rose-500", "bg-cyan-500", "bg-slate-500"];

export default function Analytics() {
  const { t } = useTranslation();
  const { currentOrg } = useOrg();
  const [data, setData] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/analytics/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/analytics/feedback").then((r) => setFeedback(r.data)).catch(() => {});
  };
  useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

  const genFeedback = async () => {
    setBusy(true);
    try { const { data } = await api.post("/analytics/feedback/generate"); setFeedback(data); toast.success(t("analytics.feedbackGenerated")); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const promote = async (id) => {
    try { const { data } = await api.post(`/analytics/feedback/${id}/promote`); toast.success(t("analytics.promoted")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const dismiss = async (id) => {
    try { await api.post(`/analytics/feedback/${id}/dismiss`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <div data-testid="analytics-page" className="p-4 text-sm text-muted-foreground">{t("common.loading") || "Loading…"}</div>;

  const maxTs = Math.max(1, ...data.timeseries.map((d) => d.count));
  const totalCat = Math.max(1, data.categories.reduce((a, c) => a + c.count, 0));
  const maxFunnel = Math.max(1, ...data.funnel.map((f) => f.count));
  const openFeedback = feedback.filter((f) => f.status === "suggested");

  const Stat = ({ Icon, label, value, slug }) => (
    <div className="bg-card border border-border rounded-sm p-5" data-testid={`analytics-stat-${slug}`}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2"><Icon size={16} /><span className="overline text-xs">{label}</span></div>
      <div className="font-heading text-3xl font-bold">{value}</div>
    </div>
  );

  return (
    <div data-testid="analytics-page">
      <PageHeader title={t("analytics.title")} subtitle={t("analytics.subtitle")} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat Icon={Stack} slug="events" label={t("analytics.totalEvents")} value={data.summary.total_events} />
        <Stat Icon={TrendUp} slug="business" label={t("analytics.businessEvents")} value={data.summary.business_events} />
        <Stat Icon={Pulse} slug="types" label={t("analytics.distinctTypes")} value={data.summary.distinct_types} />
        <Stat Icon={Lightbulb} slug="feedback" label={t("analytics.openSuggestions")} value={data.feedback_open} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="rounded-sm flex-wrap h-auto">
          <TabsTrigger value="overview" className="rounded-sm" data-testid="tab-overview"><ChartLineUp size={15} className="mr-1" />{t("analytics.overview")}</TabsTrigger>
          <TabsTrigger value="kpis" className="rounded-sm" data-testid="tab-kpis"><Target size={15} className="mr-1" />{t("analytics.kpis")}</TabsTrigger>
          <TabsTrigger value="feedback" className="rounded-sm" data-testid="tab-feedback"><Lightbulb size={15} className="mr-1" />{t("analytics.feedbackLoop")}</TabsTrigger>
          <TabsTrigger value="ml" className="rounded-sm" data-testid="tab-ml"><Flask size={15} className="mr-1" />{t("analytics.mlLayer")}</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-4 space-y-6">
          <div className="bg-card border border-border rounded-sm p-6" data-testid="timeseries-chart">
            <div className="overline text-muted-foreground mb-4">{t("analytics.eventVolume")}</div>
            <div className="flex items-end gap-1.5 h-40">
              {data.timeseries.length === 0 && <span className="text-sm text-muted-foreground">{t("analytics.noData")}</span>}
              {data.timeseries.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
                  <div className="w-full bg-primary/80 hover:bg-primary rounded-t-sm transition-all" style={{ height: `${(d.count / maxTs) * 100}%` }} title={`${d.date}: ${d.count}`} />
                  <span className="text-[9px] text-muted-foreground rotate-45 origin-left mt-1 whitespace-nowrap">{d.date.slice(5)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-sm p-6" data-testid="categories-chart">
              <div className="overline text-muted-foreground mb-4">{t("analytics.byCategory")}</div>
              <div className="space-y-2">
                {data.categories.slice(0, 8).map((c, i) => (
                  <div key={c.category} className="flex items-center gap-3">
                    <span className="text-xs font-mono w-28 truncate">{c.category}</span>
                    <div className="flex-1 bg-secondary rounded-sm h-4 overflow-hidden"><div className={`h-full ${CAT_COLORS[i % CAT_COLORS.length]}`} style={{ width: `${(c.count / totalCat) * 100}%` }} /></div>
                    <span className="text-xs font-mono text-muted-foreground w-10 text-right">{c.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border rounded-sm p-6" data-testid="funnel-chart">
              <div className="overline text-muted-foreground mb-4">{t("analytics.funnel")}</div>
              <div className="space-y-3">
                {data.funnel.map((f) => (
                  <div key={f.step}>
                    <div className="flex justify-between text-xs mb-1"><span>{f.step}</span><span className="font-mono text-muted-foreground">{f.count}</span></div>
                    <div className="bg-secondary rounded-sm h-6 overflow-hidden"><div className="h-full bg-primary/70 flex items-center px-2" style={{ width: `${Math.max(4, (f.count / maxFunnel) * 100)}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* KPIs */}
        <TabsContent value="kpis" className="mt-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kpis-grid">
            {data.kpis.length === 0 && <div className="text-sm text-muted-foreground col-span-full py-4">{t("analytics.noKpis")}</div>}
            {data.kpis.map((k) => (
              <div key={k.id} className="bg-card border border-border rounded-sm p-5" data-testid={`kpi-${k.id}`}>
                <div className="flex items-start justify-between">
                  <span className="text-sm font-medium">{k.name}</span>
                  <Badge variant={k.status === "on_track" ? "default" : "destructive"} className="rounded-sm text-[10px] gap-1">
                    {k.status === "on_track" ? <CheckCircle size={11} /> : <XCircle size={11} />}{k.status}
                  </Badge>
                </div>
                <div className="font-heading text-2xl font-bold mt-2">{k.value}</div>
                <div className="text-[11px] font-mono text-muted-foreground">{t("analytics.target")} {k.comparator} {k.target} · {k.attainment_pct ?? "—"}%</div>
                <div className="bg-secondary rounded-sm h-2 overflow-hidden mt-2"><div className={`h-full ${k.status === "on_track" ? "bg-emerald-500" : "bg-rose-500"}`} style={{ width: `${Math.min(100, k.attainment_pct || 0)}%` }} /></div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* FEEDBACK LOOP */}
        <TabsContent value="feedback" className="mt-4 space-y-4">
          <div className="flex items-center justify-between bg-card border border-border rounded-sm p-4">
            <div className="text-xs text-muted-foreground max-w-2xl">{t("analytics.feedbackNote")}</div>
            <Button onClick={genFeedback} disabled={busy} className="rounded-sm" data-testid="generate-feedback-button"><Lightbulb size={16} className="mr-2" />{busy ? t("analytics.analyzing") : t("analytics.analyze")}</Button>
          </div>
          <div className="space-y-3" data-testid="feedback-list">
            {feedback.length === 0 && <div className="text-sm text-muted-foreground py-4">{t("analytics.noFeedback")}</div>}
            {feedback.map((f) => (
              <div key={f.id} className="bg-card border border-border rounded-sm p-5" data-testid={`feedback-${f.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="rounded-sm text-[10px] capitalize">{f.category}</Badge>
                      <span className="font-medium text-sm">{f.title}</span>
                      <Badge variant={f.status === "suggested" ? "secondary" : f.status === "promoted" ? "default" : "outline"} className="rounded-sm text-[10px]">{f.status}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{f.rationale}</p>
                    <p className="text-xs mt-2 flex items-start gap-1"><ArrowRight size={13} className="mt-0.5 text-primary shrink-0" />{f.recommendation}</p>
                    {f.status === "promoted" && <div className="text-[11px] font-mono text-emerald-600 mt-2">→ {t("analytics.handedToResearch")} ({f.research_ref?.slice(0, 8)})</div>}
                  </div>
                  {f.status === "suggested" && (
                    <div className="flex flex-col gap-2 shrink-0">
                      <Button size="sm" className="rounded-sm" onClick={() => promote(f.id)} data-testid={`promote-${f.id}`}><Flask size={14} className="mr-1" />{t("analytics.toResearch")}</Button>
                      <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => dismiss(f.id)} data-testid={`dismiss-${f.id}`}>{t("analytics.dismiss")}</Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* ML LAYER */}
        <TabsContent value="ml" className="mt-4">
          <div className="bg-card border border-border rounded-sm p-6" data-testid="ml-layer">
            <div className="overline text-muted-foreground mb-1">{t("analytics.mlLayer")}</div>
            <p className="text-xs text-muted-foreground mb-4">{t("analytics.mlNote")}</p>
            <MLModels />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MLModels() {
  const { t } = useTranslation();
  const [models, setModels] = useState([]);
  const [pred, setPred] = useState(null);
  useEffect(() => { api.get("/analytics/ml/models").then((r) => setModels(r.data)).catch(() => {}); }, []);
  const runDemo = async () => {
    try { const { data } = await api.post("/analytics/ml/predict", { task: "forecast", series: [10, 12, 14, 16, 18] }); setPred(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-3">
      {models.map((m) => (
        <div key={m.id} className="flex items-center justify-between border-b border-border last:border-0 py-2" data-testid={`ml-model-${m.task}`}>
          <div><div className="text-sm font-medium font-mono">{m.id}</div><div className="text-[11px] text-muted-foreground">{m.note}</div></div>
          <Badge variant="outline" className="rounded-sm text-[10px]">{t("analytics.notTrained")}</Badge>
        </div>
      ))}
      <div className="pt-2">
        <Button size="sm" variant="outline" className="rounded-sm" onClick={runDemo} data-testid="ml-predict-demo">{t("analytics.runDemo")}</Button>
        {pred && <span className="ml-3 text-xs font-mono text-muted-foreground">{pred.method}: {pred.prediction} (trained: {String(pred.trained)})</span>}
      </div>
    </div>
  );
}
